import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Image as ImageIcon, Mic, MicOff, Send, ArrowLeft } from "lucide-react";
import api, { apiError, buildWsUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { rememberOpenChat } from "@/lib/chatSession";

const TOKEN_KEY = (roomId) => `stb_chat_token_${roomId}`;

export default function ChatPage() {
  const { roomId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Resolve token: URL param > localStorage
  const initialToken = searchParams.get("token") || localStorage.getItem(TOKEN_KEY(roomId)) || "";
  const [token, setToken] = useState(initialToken);

  // If URL has token, persist to localStorage and clean the URL.
  useEffect(() => {
    const urlToken = searchParams.get("token");
    if (urlToken) {
      localStorage.setItem(TOKEN_KEY(roomId), urlToken);
      setToken(urlToken);
      const sp = new URLSearchParams(searchParams);
      sp.delete("token");
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  if (!roomId) {
    return (
      <Layout>
        <div className="stb-card p-6" data-testid="chat-missing-room">
          <p className="stb-h text-white">Vyber konverzaci</p>
          <p className="text-[#A3A3A3] text-sm mt-1">
            Otevři chat z konkrétního inzerátu nebo ze své správy inzerátů.
          </p>
        </div>
      </Layout>
    );
  }

  if (!token) {
    return (
      <Layout>
        <div className="stb-card p-6" data-testid="chat-no-token">
          <p className="stb-h text-white">Pro otevření chatu chybí přístupový token.</p>
          <p className="text-[#A3A3A3] text-sm mt-2">
            Otevři ho z inzerátu (kupující) nebo ze správy inzerátu (prodejce).
          </p>
          <button onClick={() => navigate(-1)} className="stb-btn stb-btn-secondary mt-4">
            <ArrowLeft size={14} /> Zpět
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ChatRoom roomId={roomId} token={token} />
    </Layout>
  );
}

function ChatRoom({ roomId, token }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [meta, setMeta] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [myIdentity, setMyIdentity] = useState(null);
  const [connected, setConnected] = useState(false);
  const [onlineRoles, setOnlineRoles] = useState({ buyer: false, seller: false });
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState("");
  const wsRef = useRef(null);
  const scrollerRef = useRef(null);
  const recorderRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/chats/${roomId}/messages`, {
          params: { token },
        });
        if (cancelled) return;
        setMessages(data.items);
        setMeta(data.chat);
        setMyRole(data.my_role);
        setMyIdentity(data.my_identity);
        rememberOpenChat({
          roomId,
          token,
          listingTitle: data.chat?.listing_title || "",
          listingId: data.chat?.listing_id || "",
          otherName: data.my_role === "seller" ? (data.chat?.buyer_name || "Zájemce") : "Prodejce",
        });
      } catch (e) {
        setError(apiError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roomId, token]);

  useEffect(() => {
    if (!token || !roomId) return;
    const url = buildWsUrl(`/chats/ws/${roomId}`, token);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.event === "message" && data.data) {
          setMessages((prev) => (prev.some((m) => m.id === data.data.id) ? prev : [...prev, data.data]));
        }
        if (data.event === "presence") {
          if (data.roles && typeof data.roles === "object") {
            setOnlineRoles({
              buyer: !!data.roles.buyer,
              seller: !!data.roles.seller,
            });
          } else if (data.role === "buyer" || data.role === "seller") {
            setOnlineRoles((prev) => ({ ...prev, [data.role]: !!data.online }));
          }
        }
        if (data.type === "error") {
          setError(data.message || "Chyba");
        }
      } catch (_) {}
    };
    return () => {
      try { ws.close(); } catch (_) {}
    };
  }, [token, roomId]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  const pushLocal = (msg) => {
    if (!msg?.id) return;
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  };

  const sendPayload = async (body) => {
    setError("");
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify(body));
      return true;
    }
    const { data } = await api.post(`/chats/${roomId}/messages`, body, { params: { token } });
    pushLocal(data.message);
    return true;
  };

  const sendText = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    try {
      await sendPayload({ type: "text", text: t });
      setText("");
    } catch (err) {
      setError(apiError(err) || "Zprávu se nepodařilo odeslat.");
    }
  };

  const sendImage = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      await sendPayload({ type: "image", data_url: dataUrl, mime: "image/jpeg" });
    } catch (err) {
      setError(apiError(err) || "Fotku se nepodařilo odeslat.");
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data && e.data.size > 0 && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mime || "audio/webm" });
        const dataUrl = await blobToDataUrl(blob);
        try {
          await sendPayload({ type: "audio", data_url: dataUrl, mime: blob.type });
        } catch (err) {
          setError(apiError(err) || "Hlasovou zprávu se nepodařilo odeslat.");
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      setError("Nepovolen přístup k mikrofonu.");
    }
  };

  const stopRec = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    setRecording(false);
  };

  const otherLabel = myRole === "seller"
    ? (meta?.buyer_name || "Zájemce")
    : "Prodejce";
  const peerOnline = myRole === "seller" ? onlineRoles.buyer : myRole === "buyer" ? onlineRoles.seller : false;

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="stb-btn stb-btn-ghost text-[#0E0E0E] mb-3">
        <ArrowLeft size={14} /> Zpět
      </button>

      <section className="stb-card flex flex-col h-[600px]">
        <div className="p-3 border-b-2 border-[#262626] flex items-center justify-between">
          <div className="min-w-0">
            <p className="stb-label">Chat · {otherLabel}</p>
            {meta && (
              <Link to={`/inzerat/${meta.listing_id}`} className="text-white stb-h text-base truncate block hover:text-[#FF6B00]">
                {meta.listing_title}
              </Link>
            )}
          </div>
          <span
            className={`stb-mono text-xs ${peerOnline ? "text-[#22C55E]" : "text-[#A3A3A3]"}`}
            data-testid="chat-peer-state"
            title={connected ? "Chat je připojený" : "Zprávy se odešlou i bez živého spojení"}
          >
            {peerOnline ? `● ${otherLabel} je u chatu` : `○ ${otherLabel} teď není u chatu`}
          </span>
        </div>

        <div ref={scrollerRef} className="flex-1 p-3 space-y-2 overflow-y-auto" data-testid="chat-messages">
          {messages.map((m) => {
            const own = m.sender_id === myIdentity;
            return (
              <div key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[75%] border-2 px-3 py-2 ${
                    own ? "bg-[#FF6B00] text-[#0E0E0E] border-[#FF6B00]" : "bg-[#1a1a1a] text-white border-[#262626]"
                  }`}
                >
                  {m.type === "text" && <p className="whitespace-pre-wrap text-sm">{m.text}</p>}
                  {m.type === "image" && (
                    <img src={m.data_url} alt="" className="max-h-60 object-cover" />
                  )}
                  {m.type === "audio" && (
                    <audio controls src={m.data_url} className="w-56" />
                  )}
                  <p className={`stb-mono text-[10px] mt-1 ${own ? "text-[#0E0E0E]" : "text-[#A3A3A3]"}`}>
                    {new Date(m.created_at).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            );
          })}
          {messages.length === 0 && (
            <p className="text-center stb-label py-10">Pošli první zprávu</p>
          )}
        </div>

        {error && (
          <div className="px-3 text-[#FF6B00] text-xs stb-mono pb-1" data-testid="chat-error">{error}</div>
        )}

        <form onSubmit={sendText} className="p-3 border-t-2 border-[#262626] flex gap-2" data-testid="chat-input-form">
          <label className="stb-btn stb-btn-secondary !px-3 cursor-pointer">
            <ImageIcon size={16} />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) sendImage(f);
                e.target.value = "";
              }}
              data-testid="chat-image-input"
            />
          </label>
          <button
            type="button"
            onClick={recording ? stopRec : startRec}
            className={`stb-btn !px-3 ${recording ? "stb-btn-primary" : "stb-btn-secondary"}`}
            data-testid="chat-mic-btn"
          >
            {recording ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
          <input
            className="stb-input flex-1"
            placeholder="Napiš zprávu…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="chat-text-input"
          />
          <button className="stb-btn stb-btn-primary" disabled={!text.trim()} data-testid="chat-send-btn">
            <Send size={16} />
          </button>
        </form>
      </section>
    </div>
  );
}

function compressImage(file, maxDim = 1280, quality = 0.78) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
